// composables/useSupplierPreview.js
//
// The collector's per-shop supplier preview (supplier-preview P2-A, design
// 2026-10-20 Section 4): from a shop's page in his day, a collector reloads his
// phone as that shop's showroom, read-only, under a banner with one way out.
//
// The preview is a key in his cookie session (backend L-A); what changes after
// enter/exit is what the profile says (`meta.supplierPreview`, the showroom
// stage, the shop's org id). So both reuse the act-as discipline: purge the
// cached identity (`poolit:auth`, the store, the realtime socket), then a hard
// load — cold-boot Priority 3 re-reads the profile as it now stands. Not a
// plain reload: the load goes straight to where he must land (/suppliers/ on
// enter, his day /collector/ on exit), because F7 restores the page of a
// reloaded history entry over any boot rewrite (useActAs `hardNavigate`).
//
// WITH NO SIGNAL (supplier-preview-offline P2-A, locked answers L-A, L-I, L-K):
// the preview opens from the copy the morning download saved
// (collector/previewReplay.js) and never touches the server key. Enter saves
// his own `poolit:auth` (`poolit:preview:own-auth`), sets the marker
// `poolit:preview:replay` and fills the identity store from the copy's
// profile — read through the replay, so the guards, the banner and the boot
// rewrite see a preview exactly as for a live one. Exit removes the marker and
// puts his own identity back byte for byte: no server, no purge, and his day
// boots from the phone instead of /login/. The live preview also saves his
// identity before its purge now, so a live exit with no signal lands on his day
// too and leaves the DELETE owed (`poolit:preview:exit-pending`), which
// collector/previewLeads.js sends on the tap queue's triggers.
import { f7 } from 'framework7-vue';
import { computed, nextTick, ref } from 'vue';
import { CLUB_MANIFEST } from '@/clubs/manifest.js';
import {
  EXIT_PENDING_KEY,
  OWN_AUTH_KEY,
  readReplayMarker,
  REPLAY_MARKER_KEY,
  stopHidingMissingImages,
} from '@/collector/previewReplay.js';
import i18n from '@/i18n';
import { enterPreview, exitPreview, noteInterest } from '@/services/supplierPreview.service.js';
import { AUTH_PERSIST_KEY, useAuthStore } from '@/stores/authStore.js';

const t = (key) => i18n.global.t(key);

// Module-scoped: the shop page, the banner and the gains page share one flag,
// so a second tap anywhere waits for the load the first one started.
const busy = ref(false);

function toast(text) {
  try {
    f7.toast.create({ text, position: 'top', closeTimeout: 3000 }).open();
  } catch {
    /* f7 not mounted (tests / early boot) — the toast is best-effort */
  }
}

/**
 * The act-as purge and hard load, loaded when they are needed: useActAs pulls
 * the platform layer (and the PWA register module) in, which the gains page and
 * the lead sheet — they only send the lead — must not carry.
 */
async function actAsHelpers() {
  const { hardNavigate, useActAs } = await import('@/composables/useActAs.js');
  return { hardNavigate, purgeIdentityStorage: useActAs().purgeIdentityStorage };
}

/**
 * Back to his day — never the /suppliers/ URL the preview leaves in the address
 * bar: with the preview gone, a collector with no club membership would be
 * bounced from there to onboarding (design Section 4.5).
 */
async function loadDay() {
  (await actAsHelpers()).hardNavigate(CLUB_MANIFEST.collectors.root);
}

/** No answer from the server at all (axios puts no `response` on a network error). */
const isNetworkError = (error) => error?.response == null;

function storageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage blocked: nothing to keep */
  }
}

/** The shop's saved copy when it may be opened (under 24 hours), else null. */
async function usableCopy(organizationId) {
  try {
    const { isUsableCopy, previewStore } = await import('@/collector/previewStore.js');
    const row = await previewStore().get(organizationId);
    return isUsableCopy(row) ? row : null;
  } catch (error) {
    console.warn('[useSupplierPreview] the saved copy could not be read', error);
    return null;
  }
}

/** The shared network detector of the collector's screens — never the POST's own timeout (L-I). */
async function detectorOnline() {
  const { collectorNetwork } = await import('@/collector/network.js');
  return collectorNetwork().isOnline.value !== false;
}

/**
 * Open the preview from the copy (L-A). The marker turns replay on, so the
 * profile read below is answered by the copy; it must name the row's own shop
 * (design Section 2.G.3), else nothing changes.
 */
async function enterOffline(authStore, row) {
  const ownAuth = storageGet(AUTH_PERSIST_KEY);
  storageSet(REPLAY_MARKER_KEY, JSON.stringify({ shopId: String(row.shopId), recordedAt: row.recordedAt }));
  try {
    const { fetchUserProfile } = await import('@/composables/useAuth.js');
    const { user, delegation } = await fetchUserProfile();
    if (user?.meta?.supplierPreview?.active !== true || String(user.meta.supplierPreview.shop?.id) !== String(row.shopId)) {
      throw new Error(`the copy of shop ${row.shopId} is not that shop's preview`);
    }
    storageSet(OWN_AUTH_KEY, ownAuth);
    authStore.setSession(user, delegation);
    authStore.$persist?.(); // the shop's profile is on disk before the page goes
  } catch (error) {
    storageSet(REPLAY_MARKER_KEY, null);
    console.warn('[useSupplierPreview] the offline preview could not open', error);
    return false;
  }
  await nextTick();
  (await actAsHelpers()).hardNavigate(CLUB_MANIFEST.suppliers.root);
  return true;
}

/**
 * Put his own identity back as it was at enter, byte for byte, and drop the
 * saved copy of it. False when there is none to put back.
 */
function restoreOwnAuth() {
  const ownAuth = storageGet(OWN_AUTH_KEY);
  if (ownAuth == null) return false;
  storageSet(AUTH_PERSIST_KEY, ownAuth);
  storageSet(OWN_AUTH_KEY, null);
  // The old page lives until the new one commits: a late store write (a
  // replayed profile refresh, a loading flag) would persist the shop's identity
  // over his. `pagehide` runs after them — his identity is the last word.
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => storageSet(AUTH_PERSIST_KEY, ownAuth), { once: true });
  }
  return true;
}

export function useSupplierPreview() {
  const authStore = useAuthStore();

  const isActive = computed(() => authStore.isSupplierPreview);
  const shopName = computed(() => authStore.supplierPreview?.shop?.name ?? '');
  const shopPhone = computed(() => authStore.supplierPreview?.shop?.phone ?? '');
  const interestSent = computed(() => authStore.supplierPreviewInterestSent);

  /**
   * Open the preview of one shop — the button decides by itself (L-I): no
   * signal and a usable copy → from the copy; signal → the live POST as before;
   * the POST gets no answer and a copy exists → from the copy at once; no
   * signal and no copy → «العرض ده مش متحمّل على الموبايل — محتاج نت». A failed
   * live POST otherwise shows one toast and changes nothing.
   *
   * @param {string|number} organizationId the shop's org id (`shop.supplierId`)
   * @returns {Promise<boolean>} true right before the load
   */
  async function enter(organizationId) {
    if (busy.value) return false;
    busy.value = true;
    const copy = await usableCopy(organizationId);
    const offline = async () => {
      if (await enterOffline(authStore, copy)) return true;
      busy.value = false;
      toast(t('common.errorTryAgain'));
      return false;
    };

    // Read before the POST: a profile refresh landing during it would persist the preview.
    const ownAuth = storageGet(AUTH_PERSIST_KEY);
    if (!(await detectorOnline())) {
      if (copy) return offline();
      busy.value = false;
      toast(t('collector.preview.notSaved'));
      return false;
    }
    try {
      await enterPreview({ organizationId });
    } catch (error) {
      if (isNetworkError(error) && copy) return offline();
      busy.value = false;
      if (error?.response?.status === 401) return false; // the global handler signs out
      toast(isNetworkError(error) ? t('collector.preview.noSignal') : t('common.errorTryAgain'));
      return false;
    }
    const { purgeIdentityStorage, hardNavigate } = await actAsHelpers();
    await purgeIdentityStorage();
    // Saved AFTER the purge's write but from BEFORE it: his own identity, for an exit with no signal (L-K).
    storageSet(OWN_AUTH_KEY, ownAuth);
    // This preview replaces any closed one still owed a DELETE (that DELETE would now close this one),
    // and any marker a dead tab left (it would send this live exit down the offline path).
    storageSet(EXIT_PENDING_KEY, null);
    storageSet(REPLAY_MARKER_KEY, null);
    hardNavigate(CLUB_MANIFEST.suppliers.root);
    return true;
  }

  /**
   * Close the preview. The DELETE is best effort (204 always; the key also dies
   * on its own after 2 hours): a failure still purges and reloads, and if the
   * key survived, the banner simply shows again for another try.
   */
  async function exit() {
    if (busy.value) return;
    busy.value = true;
    const { hardNavigate, purgeIdentityStorage } = await actAsHelpers();
    const day = CLUB_MANIFEST.collectors.root;

    // From the copy: nothing on the server to close (L-A). His identity goes
    // back and the page goes in the same task — no write can land between.
    if (storageGet(REPLAY_MARKER_KEY) != null) {
      storageSet(REPLAY_MARKER_KEY, null);
      stopHidingMissingImages();
      if (!restoreOwnAuth()) await purgeIdentityStorage();
      hardNavigate(day);
      return;
    }

    try {
      await exitPreview();
      storageSet(OWN_AUTH_KEY, null);
    } catch (error) {
      console.warn('[useSupplierPreview] exit DELETE failed:', error?.message);
      // No signal: his day boots from the phone, the DELETE is owed (L-K).
      if (isNetworkError(error) && restoreOwnAuth()) {
        storageSet(EXIT_PENDING_KEY, String(Date.now()));
        hardNavigate(day);
        return;
      }
    }
    await purgeIdentityStorage();
    hardNavigate(day);
  }

  /**
   * The preview ended without the banner — the 2 hours ran out and a profile
   * refresh read `{active: false}`. The banner is gone with it, so nothing on
   * screen leads back; take him to his day the same way exit does. A sign-out
   * (401) also drops the flag, but then he is no longer authenticated and the
   * global handler owns the way to /login/.
   */
  function onEndedElsewhere() {
    if (busy.value || !authStore.isAuthenticated) return;
    busy.value = true;
    loadDay().catch((err) => console.warn('[useSupplierPreview] load failed:', err));
  }

  /**
   * «ابعت للمسؤول» — the lead to the leader's topic. Throws on failure so the
   * sheet stays open; on success the store remembers it for the done line.
   * From the copy (no signal, L-L) the lead is written onto the shop's copy and
   * sent by collector/previewLeads.js once the server answers: 'saved'.
   *
   * @param {string} [phone] what the collector typed (optional)
   * @returns {Promise<'sent'|'saved'>}
   */
  async function sendInterest(phone) {
    const marker = readReplayMarker();
    if (marker) {
      const { saveOfflineLead } = await import('@/collector/previewLeads.js');
      await saveOfflineLead(marker.shopId, phone);
    } else {
      await noteInterest({ phone: phone?.trim() || null });
    }
    authStore.markSupplierPreviewInterestSent();
    return marker ? 'saved' : 'sent';
  }

  return {
    isActive,
    shopName,
    shopPhone,
    interestSent,
    busy,
    enter,
    exit,
    sendInterest,
    onEndedElsewhere,
  };
}
